import { useContext, useLayoutEffect, useMemo, useRef } from "react";
import cx from "classix";
import { AuthContext } from "../../context/auth";
import "./stylesLoader.scss";

// Nunca colapsa por debajo de esto, ni con un contenedor diminuto (letra+puntos miden ~170px).
const PISO_MINIMO_PX = 220;

const resolverLetra = (pais) => {
  if (pais === "Chile") return "w";
  if (pais === "Perú") return "v";
  // pais todavía no definido (sesión cargando, o Loader montado fuera del AuthProvider): fallback por hostname.
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  return hostname.includes("visiona") ? "v" : "w";
};

// Ancestro más cercano que recorta o scrollea su contenido (modal, celda, panel con alto fijo,
// o el <body> de una ventana aparte como FichaEdificioWindow) — ahí es donde el Loader se centra,
// sin estirarse más allá. Si no hay ninguno, el límite pasa a ser el borde inferior del viewport.
// `ownWindow` viene de getBoundingClientRect: getComputedStyle debe correr contra la ventana DUEÑA
// del nodo (una ventana aparte no comparte realm con la ventana principal donde corre el script).
const buscarAncestroAcotado = (el, ownWindow) => {
  let nodo = el.parentElement;
  while (nodo) {
    const estilo = ownWindow.getComputedStyle(nodo);
    if (estilo.overflowY !== "visible" || estilo.overflow !== "visible") return nodo;
    nodo = nodo.parentElement;
  }
  return null;
};

export const Loader = ({ fullPage = false, className }) => {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (fullPage) return; // fullPage ya fija min-height:100vh por CSS (.full) — no necesita medición.
    const el = rootRef.current;
    if (!el) return;

    const ownWindow = el.ownerDocument?.defaultView || window;

    const recalcular = () => {
      const top = el.getBoundingClientRect().top;
      const ancestroAcotado = buscarAncestroAcotado(el, ownWindow);
      const limiteInferior = ancestroAcotado ? ancestroAcotado.getBoundingClientRect().bottom : ownWindow.innerHeight;
      el.style.minHeight = `${Math.max(PISO_MINIMO_PX, limiteInferior - top)}px`;
    };

    recalcular();
    ownWindow.addEventListener("resize", recalcular);
    return () => ownWindow.removeEventListener("resize", recalcular);
  }, [fullPage]);

  // useContext(AuthContext) crudo en vez de useAuthContext(): ese wrapper lanza si no hay
  // AuthProvider arriba, y envolverlo en try/catch viola react-hooks/rules-of-hooks (hook
  // condicional). Leyendo el contexto directo, si no hay provider da undefined sin romper
  // — mismo fallback por hostname cubre ese caso.
  const auth = useContext(AuthContext);
  const letra = useMemo(() => resolverLetra(auth?.pais), [auth?.pais]);

  return (
    <div ref={rootRef} className={cx("Loader", fullPage && "full", className)}>
      <div className="Loader-final">
        <div className="Loader-letra">
          <div className="sombra" />
          <div className="w">{letra}</div>
        </div>
        <div className="dotsN">
          <b />
          <b />
          <b />
        </div>
      </div>
    </div>
  );
};
